<template>
  <dead-deal-message-box
    v-if="deal.death && deal.status === STATUS_DEAD"
    :death="deal.death"
  />
  <div
    v-if="deal.id"
    id="deal-header"
    class="sticky top-0 z-30 flex-col bg-white px-8 pb-6 pt-[18px] text-left shadow-sm"
  >
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-3 overflow-hidden">
        <back-button class="pr-5" :route="backButtonRedirectRoute" />
        <header-application-name
          :id="
            deal?.workflow_segment === WORKFLOW_SEGMENT_BUSINESS
              ? deal?.business?.profile_id
              : deal?.personal_information?.profile_id
          "
          :name="deal?.application_name"
          :segment="deal?.workflow_segment"
        />
        <template v-if="!simplified">
          <priority-select
            v-model="applicationPriority"
            v-tooltip="$t('DEALS.APPLICATION_PRIORITY')"
            :show-value="false"
            :teleport-options="{ enabled: true }"
            class="max-w-16"
          />
          <deal-statuses-dropdown />
          <deal-products-requested-dropdown />
        </template>
      </div>
      <div class="flex items-center">
        <div v-if="isNotDead">
          <div v-if="!isActiveDealDataCapture" class="flex items-center">
            <dead-deal-modal
              v-if="showKillDealModal"
              :close="() => (showKillDealModal = false)"
            />
            <color-button
              v-if="canEditDealProgress"
              type="error"
              data-cy="dead-button"
              @click="showKillDealModal = true"
            >
              {{ $t("COMMON.MARK_AS_DEAD") }}
            </color-button>
            <color-button
              v-if="canMoveStages && showSubmitToNextPhase"
              :disabled="updatingStatus"
              class="ml-2 capitalize"
              data-cy="move-stage-button"
              @click="submitToPhase"
            >
              {{ $t("COMMON.SUBMIT") }} to {{ nextPhase }}
            </color-button>
          </div>
        </div>
        <div
          v-if="!isNotDead && canEditDealProgress"
          class="flex space-x-4 text-right"
        >
          <color-button data-cy="restore-button" @click="restoreApp">
            {{ $t("COMMON.RESTORE") }}
          </color-button>
        </div>
        <deal-activity-button v-if="!isActiveDealDataCapture" />
        <deal-header-menu
          v-if="!isClientUser"
          :delete-redirect-route="backButtonRedirectRoute"
          @rollback="handleRollback"
        />
      </div>
    </div>
    <div v-if="!simplified" class="space-y-5">
      <div class="flex flex-wrap gap-8 pl-[53px] text-sm text-gray-400">
        <div class="client-name flex items-center gap-x-1">
          <div class="flex items-center space-x-2">
            <icon-base
              height="24"
              width="24"
              view-box="0 0 48 48"
              :icon="IconCompany"
            />
            <span data-cy="client-name">
              {{ clientNameWithBrand }}
            </span>
          </div>
          <copy-to-clipboard-button
            v-if="clientNameWithBrand"
            class="copy-button"
            :value="clientNameWithBrand"
          />
        </div>
        <div class="flex items-center">
          <icon-base :icon="IconCalendar" :icon-color="UTIL_COLORS.DEFAULT" />
          <span class="mt-5 ml-2">
            <date-and-time-display :value="deal.created_at || ''" />
          </span>
        </div>
        <div v-if="industryType" class="flex items-center space-x-2">
          <icon-base height="16" width="16" :icon="IconBriefcase" />
          <span>
            {{ industryType }}
          </span>
        </div>
        <onboarding-processor-analyst />
      </div>
      <app-phase-chart ref="appPhaseChartRef" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import * as Sentry from "@sentry/vue";
import { useMutation } from "@tanstack/vue-query";

import { AnalyticsEvent } from "@/enums/analytics";
import { Ability, PermissionSubject } from "@/enums/auth";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import {
  CATEGORY_DEAL_MOVE_FORWARD,
  CATEGORY_DEAL_RESTORE,
  CATEGORY_DEAL_ROLLBACK,
  MESSAGE_APP_ROLLBACK,
  MESSAGE_DEAL_MOVE_FORWARD,
  MESSAGE_RESTORE_APP,
} from "@/helpers/constants/sentry";
import { getBackButtonRedirectRoute } from "@/helpers/router";
import { useAuth, usePermissions } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { useIndustryOptions } from "@/hooks/options";
import {
  useActiveWorkflowTemplateStages,
  useWorkflowFields,
} from "@/hooks/workflow";
import { ROUTE_DEALS } from "@/router/routes";
import { useAnalyticsStore } from "@/stores/analytics";

import CopyToClipboardButton from "@/components/CopyToClipboardButton.vue";
import DealProductsRequestedDropdown from "@/components/deals/DealProductsRequestedDropdown.vue";
import DealStatusesDropdown from "@/components/deals/DealStatusesSelector.vue";
import PrioritySelect from "@/components/ui/inputs/PrioritySelect.vue";

import type { Priority } from "@/models/applications";
import type { IOffer } from "@/models/funders";
import type { IWorkflowStage } from "@/models/workflows";
</script>
<script setup lang="ts">
import capitalize from "lodash/capitalize";

import {
  PHASE_ABSTRACT_APPROVED,
  PHASE_COMPLETE,
  STATUS_DEAD,
  UTIL_COLORS,
  WORKFLOW_SEGMENT_BUSINESS,
} from "@/helpers/constants";

import HeaderApplicationName from "@/components/deals/HeaderApplicationName.vue";
import DeadDealMessageBox from "@/components/deals/dead/DeadDealMessageBox.vue";
import DeadDealModal from "@/components/deals/dead/DeadDealModal.vue";
import DealActivityButton from "@/components/deals/dealActivity/DealActivityButton.vue";
import IconBriefcase from "@/components/icons/IconBriefcase.vue";
import IconCalendar from "@/components/icons/IconCalendar.vue";
import IconCompany from "@/components/icons/IconCompany.vue";
import IconBase from "@/components/ui/IconBase.vue";
import BackButton from "@/components/ui/buttons/BackButton.vue";
import ColorButton from "@/components/ui/buttons/ColorButton.vue";
import DateAndTimeDisplay from "@/components/ui/text/DateAndTimeDisplay.vue";
import AppPhaseChart from "@/views/deals/components/AppPhaseChart.vue";
import DealHeaderMenu from "@/views/deals/components/DealHeaderMenu.vue";
import OnboardingProcessorAnalyst from "@/views/deals/components/OnboardingProcessorAnalyst.vue";

defineProps<{
  simplified: boolean;
}>();

const { state, getters, commit, dispatch } = useStore();
const route = useRoute();
const { t } = useI18n();
const { isClientUser } = useAuth();
const {
  activeDeal: deal,
  isActiveDealDataCapture,
  canEditDealProgress,
} = useDeals();
const {
  activeTemplateFlow,
  activeTemplateStages,
  currentWorkflowPhaseIndx,
  activeWorkflowPhases,
  indexOfCurrentPhase,
} = useActiveWorkflowTemplateStages();
const { industryTypeLabel } = useIndustryOptions();
const { fieldData } = useWorkflowFields("additional_business");
const { canPerformActionReactive } = usePermissions();
const { assignShortcutToAction } = useKeyboardShortcuts();
const { trackEvent } = useAnalyticsStore();

const updatingStatus = ref(false);
const showKillDealModal = ref(false);
const appPhaseChartRef = ref<InstanceType<typeof AppPhaseChart> | null>(null);

const clientNameWithBrand = computed(() => {
  const clientName = deal.value?.client?.name;
  const brandName = deal.value?.brand_name?.name;

  if (!clientName) {
    return "";
  }

  return brandName ? `${clientName} (${brandName})` : clientName;
});

const canMoveStages = canPerformActionReactive(
  PermissionSubject.application,
  Ability.moveStages,
  { application: deal.value.id }
);

const updateApplicationPriorityMutation = useMutation({
  mutationFn: async (nextPriority: Priority) => {
    await dispatch("applications/updateApp", {
      priority: nextPriority,
    });
    await dispatch("applications/syncActiveToAll");
  },
  onError: () => {
    applicationPriority.value = deal.value.priority ?? "none";
  },
});

const backButtonRedirectRoute = computed(() =>
  getBackButtonRedirectRoute(
    route.query.backTo,
    state.previousFullRoute,
    ROUTE_DEALS
  )
);

const isNotDead = computed(() => {
  const deadStatus: string = getters["options/deadStatus"];
  return deal.value.status !== parseInt(deadStatus, 10);
});

const offers = computed<IOffer[]>(() => getters["applications/offers"]);

const nextPhase = computed(() => {
  if (
    indexOfCurrentPhase.value === -1 ||
    indexOfCurrentPhase.value === activeWorkflowPhases.value.length - 1
  ) {
    return null;
  }
  return activeTemplateStages.value[currentWorkflowPhaseIndx.value + 1].name;
});

const industryType = computed(() => {
  const industryId = fieldData.value.additional_business_industry;
  return industryId ? industryTypeLabel(industryId) : null;
});

const showSubmitToNextPhase = computed(() => {
  const next = nextPhase.value?.toLowerCase() || null;
  if (!next) {
    return false;
  }
  if (next === "closing") {
    return !!offers.value?.length;
  }
  if (next === "funded") {
    return !!offers.value?.some((o) => o.status === 1);
  }
  return true;
});

const applicationPriority = computed({
  get() {
    return deal.value.priority ?? "none";
  },
  set(nextPriority) {
    updateApplicationPriorityMutation.mutate(nextPriority);
  },
});

const handleRollback = () => {
  Sentry.addBreadcrumb({
    category: CATEGORY_DEAL_ROLLBACK,
    message: MESSAGE_APP_ROLLBACK.replace(
      ":prevStage",
      activeWorkflowPhases.value[indexOfCurrentPhase.value - 1]
    ).replace(
      ":currentStage",
      activeWorkflowPhases.value[indexOfCurrentPhase.value]
    ),
  });
  appPhaseChartRef.value?.setActivePhaseIndex(indexOfCurrentPhase.value - 1);
};

const submitToPhase = async () => {
  updatingStatus.value = true;

  const nextWorkflowPhase =
    activeTemplateStages.value[indexOfCurrentPhase.value + 1];

  let phaseToSubmitTo = nextWorkflowPhase
    ? capitalize(nextWorkflowPhase.type)
    : activeWorkflowPhases.value[indexOfCurrentPhase.value + 1];

  if (indexOfCurrentPhase.value >= activeWorkflowPhases.value.length - 1) {
    return;
  }
  if (phaseToSubmitTo === PHASE_ABSTRACT_APPROVED) {
    phaseToSubmitTo = PHASE_COMPLETE;
  }

  Sentry.addBreadcrumb({
    category: CATEGORY_DEAL_MOVE_FORWARD,
    message: MESSAGE_DEAL_MOVE_FORWARD.replace(
      ":nextStage",
      activeWorkflowPhases.value[indexOfCurrentPhase.value + 1]
    ).replace(
      ":currentStage",
      activeWorkflowPhases.value[indexOfCurrentPhase.value]
    ),
  });

  appPhaseChartRef.value?.setActivePhaseIndex(indexOfCurrentPhase.value + 1);
  try {
    await dispatch(
      "workflows/submitToNextWorkflowPhase",
      indexOfCurrentPhase.value
    );
    commit(
      "applications/updateActiveWorkflowStage",
      activeTemplateFlow.value[indexOfCurrentPhase.value + 1]?.id
    );
    commit("applications/setCurrentPhase", indexOfCurrentPhase.value);
    /** Fetch statuses for next stage */
    dispatch("applications/getWorkflowStatuses", deal.value?.id);

    const activeStage: IWorkflowStage = getters["workflows/activeStage"];
    trackEvent(AnalyticsEvent.WflDealStageChanged, {
      applicationId: deal.value.id,
      stageId: activeStage.id,
      stageName: activeStage.name,
      stageType: activeStage.type,
      isRollback: false,
    });
  } finally {
    updatingStatus.value = false;
  }
};

const restoreApp = async () => {
  try {
    await dispatch("applications/reviveActiveApp");
    commit("setGlobalMessage", {
      title: t("DEALS.DEAL_RESTORED"),
      type: "success",
    });
    Sentry.addBreadcrumb({
      category: CATEGORY_DEAL_RESTORE,
      message: MESSAGE_RESTORE_APP.replace(":app", deal.value.id),
    });
  } catch (error) {
    commit("setGlobalMessage", {
      title: t("COMMON.ERROR_OCCURRED"),
      type: "error",
    });
  }
};

assignShortcutToAction({
  actionName: ShortcutAction.MarkDealDead,
  action: () => (showKillDealModal.value = true),
});
assignShortcutToAction({
  actionName: ShortcutAction.MarkDealDeadBackspace,
  action: () => (showKillDealModal.value = true),
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.copy-button {
  @apply invisible relative p-1 opacity-0 hover:bg-gray-100;
}

.client-name:hover .copy-button {
  @apply visible opacity-100;
}
</style>
